/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState } from 'react';
import { cloneDeep, startsWith } from 'lodash';
import { Field } from 'react-final-form';
import { StyleSheet, css } from 'aphrodite';
import { LinkButton, InputContainer, TextInput, Text } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { login } from 'blb-api/auth';
import { FormCard } from 'blb-components/FormCard';
import { SetUser } from 'blb-ui-types/login';
import { useLoginFlowSubmit, extractValue } from 'blb-utils/login';
import { getParsedCookieValue } from 'blb-utils/cookies';
import { REMEMBER_MFA_DEVICE_COOKIE } from 'blb-constants/global';
import { removeDefaultAnchorStyling } from 'blb-styles/mixins';
import { usernameInputConfig, passwordInputConfig, rememberMeInputConfig } from 'blb-inputs/unauthenticated';

import { SigninHelpModal } from './SigninHelpModal/SigninHelpModal';
import { ForgotPasswordLinkButton } from '../ForgotPasswordPage/ForgotPasswordLinkButton';

export const styles = StyleSheet.create({
  fieldWrapper: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
  linkBarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  navLink: {
    ...removeDefaultAnchorStyling(),
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    height: '100%',
  },
  spacer: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
});

export namespace SignInPage {
  export interface Props {
    setMe: SetUser;
  }
}

export const SignInPage: React.FC<SignInPage.Props> = ({ setMe }) => {
  const [isSigninHelpModalOpen, setSigninHelpModalOpen] = useState<boolean>(false);
  const { username: usernameFromParams, password: passwordFromParams } = (() => {
    const qs = window.location.search.slice(1);
    // some passwords also have & so we have to manually parse this
    const { unformattedUser, unformattedPassword } = (() => {
      if (!startsWith(qs, 'username')) {
        return { unformattedPassword: undefined, unformattedUser: undefined };
      }
      const firstArgEnd = qs.indexOf('&');
      if (firstArgEnd !== -1) {
        return { unformattedPassword: qs.slice(firstArgEnd + 1), unformattedUser: qs.slice(0, firstArgEnd) };
      } else {
        return { unformattedPassword: undefined, unformattedUser: qs };
      }
    })();

    const username = unformattedUser && extractValue(decodeURIComponent(unformattedUser));
    const password = unformattedPassword && extractValue(decodeURIComponent(unformattedPassword));
    return {
      password,
      username,
    };
  })();

  const onSubmit = useLoginFlowSubmit<BlendBridge.API.LoginBody, BlendBridge.API.LoginBody>(
    login,
    setMe,
    formValues => {
      const formattedValues = cloneDeep(formValues);
      if (usernameFromParams) formattedValues.username = usernameFromParams;
      if (passwordFromParams) formattedValues.password = passwordFromParams;
      return formattedValues;
    },
  );

  return (
    <FormCard<BlendBridge.API.LoginBody>
      additionalFooterComponent={
        !getParsedCookieValue(REMEMBER_MFA_DEVICE_COOKIE) ? <Field {...rememberMeInputConfig} /> : undefined
      }
      headerTitle="Welcome!"
      id="SignIn"
      onSubmit={onSubmit}
      showCancel={false}
      submitLabel="Sign in"
    >
      {!usernameFromParams ? (
        <Field {...usernameInputConfig} />
      ) : (
        <div className={css(styles.spacer)}>
          <InputContainer
            customComponentProps={{ children: usernameFromParams }}
            input={{ name: 'email' }}
            inputComponent={Text}
            label="Email"
            labelSize="sm"
            meta={{} as InputContainer['props']['meta']}
          />
        </div>
      )}
      {!passwordFromParams ? (
        <Field {...passwordInputConfig} />
      ) : (
        <div className={css(styles.spacer)}>
          <InputContainer
            customComponentProps={{ disabled: true, type: 'password', value: passwordFromParams }}
            input={{ name: 'password' }}
            inputComponent={TextInput}
            label="Password"
            labelSize="sm"
            meta={{} as InputContainer['props']['meta']}
          />
        </div>
      )}
      <div className={css(styles.linkBarContainer)}>
        <ForgotPasswordLinkButton usernameFromParams={usernameFromParams} />
        <div>
          <LinkButton label="Having trouble signing in?" onClick={() => setSigninHelpModalOpen(true)} />
        </div>
      </div>
      <SigninHelpModal handleClose={() => setSigninHelpModalOpen(false)} isOpen={isSigninHelpModalOpen} />
    </FormCard>
  );
};
